import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import PageFrame from "./PageFrame";
import api from "../bootstrapApi";
import { saasHeroActionButtonSx, saasSectionCardSx, saasSoftCountSx } from "../components/saasStyles";

function startOfWeek(date) {
  const next = new Date(date);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  next.setDate(next.getDate() + diff);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function formatDayLabel(date) {
  return date.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit" });
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function inWeekRange(value, weekStart) {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const weekEnd = addDays(weekStart, 7);
  return date >= weekStart && date < weekEnd;
}

export default function CalendarPage() {
  const [entries, setEntries] = useState([]);
  const [message, setMessage] = useState(null);
  const [referenceDate, setReferenceDate] = useState(() => new Date());

  useEffect(() => {
    api
      .get("/api/pmo/calendar")
      .then((response) => setEntries(response.data))
      .catch(() => setMessage({ type: "error", text: "Les donnees du calendrier n'ont pas pu etre chargees." }));
  }, []);

  const weekStart = useMemo(() => startOfWeek(referenceDate), [referenceDate]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)), [weekStart]);

  const weekEntries = useMemo(
    () =>
      entries.filter(
        (entry) =>
          inWeekRange(entry.confirmed_start || entry.option_a_start || entry.option_b_start, weekStart)
      ),
    [entries, weekStart]
  );

  const entriesByDay = useMemo(() => {
    const map = new Map(weekDays.map((day) => [day.toDateString(), []]));

    weekEntries.forEach((entry) => {
      const slotDate = new Date(entry.confirmed_start || entry.option_a_start || entry.option_b_start);
      const key = slotDate.toDateString();
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(entry);
    });

    return map;
  }, [weekDays, weekEntries]);

  const technicianLoad = useMemo(() => {
    const grouped = new Map();
    weekEntries.forEach((entry) => {
      const key = entry.technician_name || "Non affecte";
      grouped.set(key, (grouped.get(key) || 0) + 1);
    });
    return Array.from(grouped.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [weekEntries]);

  const confirmedCount = weekEntries.filter((entry) => entry.confirmed_start).length;
  const proposedCount = weekEntries.filter((entry) => !entry.confirmed_start && (entry.option_a_start || entry.option_b_start)).length;

  return (
    <PageFrame
      eyebrow="SmartDoctors"
      title="Calendrier global"
      description="Calendrier hebdomadaire central pour les propositions, les rendez-vous confirmes et la charge PMO."
      stats={[]}
      action={
        <Button variant="contained" startIcon={<CalendarMonthRoundedIcon />} sx={saasHeroActionButtonSx}>
          Vue calendrier
        </Button>
      }
    >
      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
        <Box sx={{ ...saasSectionCardSx, borderRadius: "34px", flex: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => setReferenceDate(addDays(referenceDate, -7))} sx={{ minWidth: 42, borderRadius: "12px" }}>
                <ChevronLeftRoundedIcon />
              </Button>
              <Button variant="outlined" onClick={() => setReferenceDate(addDays(referenceDate, 7))} sx={{ minWidth: 42, borderRadius: "12px" }}>
                <ChevronRightRoundedIcon />
              </Button>
            </Stack>
            <Typography variant="h6" sx={{ color: "#25324b" }}>
              Semaine du {weekStart.toLocaleDateString("fr-FR")}
            </Typography>
          </Stack>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 1.2 }}>
            {weekDays.map((day) => (
              <Box key={day.toISOString()} sx={{ border: "1px solid #e1e7f2", borderRadius: "20px", bgcolor: "#fff", minHeight: 220, p: 1.2, display: "grid", alignContent: "start", gap: 1 }}>
                <Typography variant="subtitle2" sx={{ color: "#25324b", fontWeight: 800 }}>
                  {formatDayLabel(day)}
                </Typography>
                {(entriesByDay.get(day.toDateString()) || []).length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    Aucun rendez-vous
                  </Typography>
                ) : (
                  (entriesByDay.get(day.toDateString()) || []).map((entry) => (
                    <Box key={`day-${day.toDateString()}-${entry.id}`} sx={{ borderRadius: "16px", p: 1, bgcolor: entry.confirmed_start ? "rgba(32,180,134,0.10)" : "rgba(91,103,246,0.08)", border: "1px solid #dce3f2" }}>
                      <Typography variant="caption" sx={{ display: "block", fontWeight: 800, color: "#25324b" }}>
                        #{entry.order_id} {entry.contact_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.2 }}>
                        {entry.technician_name || "Non affecte"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.2 }}>
                        {entry.confirmed_start
                          ? `${formatDateTime(entry.confirmed_start)} - ${formatDateTime(entry.confirmed_end)}`
                          : entry.option_a_start
                            ? `${formatDateTime(entry.option_a_start)} - ${formatDateTime(entry.option_a_end)}`
                            : entry.option_b_start
                              ? `${formatDateTime(entry.option_b_start)} - ${formatDateTime(entry.option_b_end)}`
                              : "-"}
                      </Typography>
                      <Chip
                        size="small"
                        label={entry.confirmed_start ? "confirme" : entry.status || "propose"}
                        sx={{
                          mt: 0.8,
                          borderRadius: "999px",
                          bgcolor: entry.confirmed_start ? "rgba(32,180,134,0.16)" : "rgba(91,103,246,0.12)",
                          color: entry.confirmed_start ? "#12805b" : "#5b67f6",
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                  ))
                )}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ ...saasSectionCardSx, borderRadius: "34px", width: { xs: "100%", lg: 320 }, display: "grid", gap: 1.5, alignContent: "start" }}>
          <Typography variant="h6" sx={{ color: "#25324b" }}>
            Charge
          </Typography>

          <Box sx={{ ...saasSoftCountSx, minWidth: "100%", minHeight: 66 }}>
            <Typography variant="caption" sx={{ color: "#7d89a2" }}>Confirmes</Typography>
            <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 800 }}>{confirmedCount} rendez-vous</Typography>
          </Box>

          <Box sx={{ ...saasSoftCountSx, minWidth: "100%", minHeight: 66 }}>
            <Typography variant="caption" sx={{ color: "#7d89a2" }}>Proposes</Typography>
            <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 800 }}>{proposedCount} rendez-vous</Typography>
          </Box>

          <Typography variant="subtitle2" sx={{ color: "#25324b", fontWeight: 800, mt: 1 }}>
            Charge techniciens
          </Typography>

          {technicianLoad.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Aucun rendez-vous cette semaine.
            </Typography>
          ) : (
            technicianLoad.map((item) => (
              <Box key={item.name} sx={{ border: "1px solid #e1e7f2", borderRadius: "16px", p: 1.2, bgcolor: "#fff" }}>
                <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 800 }}>
                  {item.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.count} rendez-vous cette semaine
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </Stack>
    </PageFrame>
  );
}
